import { ReactNode } from 'react';

import { PlayerNumber } from '~/modules/players/player-number';
import { AnnouncedQuality } from '~/routes/game/singing/game-overlay/components/pick-feedback';
import ScoreText from '~/routes/game/singing/game-overlay/components/score-text';
import { ComboDisplay } from '~/routes/game/singing/game-overlay/helpers/combo-display';
import { ofFrame } from '~/routes/game/singing/game-overlay/helpers/frame-scale';
import { cn } from '~/utils/cn';

const SIZES = {
  regular: { name: 22, score: 52, bar: 64, tag: 30, tagText: 17, pill: 24, combo: 40 },
  compact: { name: 18, score: 38, bar: 48, tag: 28, tagText: 16, pill: 20, combo: 40 },
};

/** How each announced quality is dressed. Which ones are announced lives in `pickFeedback`. */
const SHOWN: Record<AnnouncedQuality, { text: string; className: string }> = {
  perfect: { text: 'Perfect!', className: 'bg-yellow-400/20 text-yellow-300 shadow-[inset_0_0_0_2px_#facc1580]' },
  good: { text: 'Great!', className: 'bg-green-400/15 text-green-400 shadow-[inset_0_0_0_2px_#4ade8073]' },
  missed: { text: 'Miss', className: 'bg-red-500/15 text-red-400 shadow-[inset_0_0_0_2px_#ef444473]' },
};

export function FeedbackPill({ quality, sizePx }: { quality: AnnouncedQuality; sizePx: number }) {
  const shown = SHOWN[quality];

  return (
    <span
      className={cn(
        'motion-preset-slide-left motion-duration-300 typography rounded-full font-bold whitespace-nowrap',
        shown.className,
      )}
      data-test="feedback-pill"
      style={{ fontSize: ofFrame(sizePx), padding: `${ofFrame(sizePx / 3)} ${ofFrame(sizePx * 0.8)}` }}>
      {shown.text}
    </span>
  );
}

function Combo({ display, height, children }: { display: ComboDisplay; height: number; children?: ReactNode }) {
  // A fixed row whatever is in it, so the score above never moves when a run starts or ends.
  return (
    <div className="flex items-center justify-end gap-2" style={{ height: ofFrame(height) }}>
      {children}
      {display?.kind === 'combo' && (
        <>
          <span
            className={cn(
              'stroke-text leading-none font-black tabular-nums',
              display.tiered ? 'text-active' : 'text-white/60',
            )}
            style={{ fontSize: ofFrame(display.sizePx) }}>
            {display.count}&times;
          </span>
          <span
            className={cn('font-bold tracking-[.08em] uppercase', display.tiered ? 'text-active' : 'text-white/45')}
            style={{ fontSize: ofFrame(15) }}>
            {display.label}
          </span>
        </>
      )}
      {display?.kind === 'lost' && (
        <span className="font-bold tracking-[.08em] text-white/30 uppercase" style={{ fontSize: ofFrame(15) }}>
          combo lost
        </span>
      )}
    </div>
  );
}

interface Props {
  playerNumber: PlayerNumber;
  name: string;
  color: string;
  score: number;
  isFirst: boolean;
  compact: boolean;
  combo: ComboDisplay;
  feedback: AnnouncedQuality | null;
  /** "· singing" in pass the mic, on whoever holds the microphone. */
  singing?: boolean;
  dim?: boolean;
}

export default function ScoreCard({
  playerNumber,
  name,
  color,
  score,
  isFirst,
  compact,
  combo,
  feedback,
  singing = false,
  dim = false,
}: Props) {
  const size = compact ? SIZES.compact : SIZES.regular;

  return (
    <div
      className={cn('flex max-w-full flex-col items-end gap-1.5', dim && 'opacity-60')}
      data-test={`player-${playerNumber}-score`}
      data-score={Math.floor(score)}>
      <div className="flex max-w-full items-center justify-end gap-3.5">
        <div className="flex min-w-0 flex-col items-end gap-0.5">
          <span className="flex max-w-full items-center gap-2">
            {isFirst && (
              <span
                className="bg-active flex shrink-0 items-center rounded-md px-[0.6em] font-black tracking-[.04em] text-black uppercase"
                style={{ height: ofFrame(size.tag), fontSize: ofFrame(size.tagText) }}
                data-test={`player-${playerNumber}-leading`}>
                1st
              </span>
            )}
            <span
              className="stroke-text truncate font-bold tracking-[.04em] whitespace-nowrap uppercase"
              style={{ color, fontSize: ofFrame(size.name), lineHeight: 1.2 }}>
              <span className="ph-no-capture">{name}</span>
              {singing && <span className="text-white/75"> &middot; singing</span>}
            </span>
          </span>
          <span
            className="stroke-text leading-none font-bold text-white tabular-nums"
            style={{ fontSize: ofFrame(size.score) }}>
            <ScoreText score={score} />
          </span>
        </div>
        <span className="w-2 shrink-0 rounded" style={{ background: color, height: ofFrame(size.bar) }} />
      </div>
      {/* Under the score rather than beside it, so it never reaches out over the lane. */}
      <Combo display={combo} height={size.combo}>
        <span className="flex" data-test={`score-feedback-${playerNumber}`}>
          {feedback && <FeedbackPill quality={feedback} sizePx={size.pill} />}
        </span>
      </Combo>
    </div>
  );
}

const ROW = { rank: 22, name: 20, score: 30, bar: 34, pill: 16, combo: 18 };

/** One line per singer, for when five to eight of them share the column beside the lane. */
export function ScoreRow({
  playerNumber,
  rank,
  name,
  color,
  score,
  isFirst,
  combo,
  feedback,
  singing = false,
  dim = false,
}: Omit<Props, 'compact'> & { rank: number }) {
  return (
    <div
      className={cn('flex w-full items-center justify-end gap-3', dim && 'opacity-60')}
      data-test={`player-${playerNumber}-score`}
      data-score={Math.floor(score)}>
      {combo?.kind === 'combo' && (
        <span
          className={cn('stroke-text font-black tabular-nums', combo.tiered ? 'text-active' : 'text-white/60')}
          style={{ fontSize: ofFrame(ROW.combo) }}>
          {combo.count}&times;
        </span>
      )}
      {/* The feedback takes the name's place for a moment, so the row keeps its width. */}
      <span className="flex min-w-0 flex-1 justify-end" data-test={`score-feedback-${playerNumber}`}>
        {feedback ? (
          <FeedbackPill quality={feedback} sizePx={ROW.pill} />
        ) : (
          <span
            className="stroke-text truncate font-bold tracking-[.04em] uppercase"
            style={{ color, fontSize: ofFrame(ROW.name) }}>
            <span className="ph-no-capture">{name}</span>
            {singing && <span className="text-white/75"> &middot;</span>}
          </span>
        )}
      </span>
      <span
        className="stroke-text min-w-[4.5em] shrink-0 text-right leading-none font-bold text-white tabular-nums"
        style={{ fontSize: ofFrame(ROW.score) }}>
        <ScoreText score={score} />
      </span>
      <span
        className={cn(
          'flex w-[1.6em] shrink-0 items-center justify-center rounded font-black tabular-nums',
          isFirst ? 'bg-active text-black' : 'text-white/70',
        )}
        style={{ fontSize: ofFrame(ROW.rank), height: ofFrame(ROW.bar), boxShadow: `inset -4px 0 0 ${color}` }}
        data-test={isFirst ? `player-${playerNumber}-leading` : undefined}>
        {rank}
      </span>
    </div>
  );
}
